/**
 * The single typed registry of every field of the Job Role wizard (docs/job-role-guided-fields-contract.md section 3.1).
 *
 * Wording, requirement level, control type and where the options come from are changed HERE and nowhere else: the components render from this
 * table, the Review step reads its "Needs attention" and "Recommended but optional" lists from it, and `npm run docs:role-fields` renders it
 * to docs/job-role-wizard-field-dictionary.md (a test fails if the checked-in file drifts).
 *
 * Rules:
 *  - It is STATIC data: no expressions, no code. Validation and visibility are declarative.
 *  - An `example` is guidance text. It is never a placeholder that becomes a value and is never saved.
 *  - Standard option lists never live in a component. A field with `optionCategory` gets its options from the reference-options API.
 *  - No generative text: every sentence below was written by a person.
 *  - This file imports nothing so a plain Node script can load it to generate the dictionary.
 */

export type FieldRequirement = 'required' | 'requiredToActivate' | 'recommended' | 'optional';

export type FieldInputType =
  | 'text' | 'textarea' | 'combobox' | 'multiselect' | 'select' | 'date' | 'money' | 'number' | 'list' | 'occupation' | 'department' | 'readonly';

export type CustomValuePolicy = 'none' | 'allowed' | 'allowedWithConfirm';

/** The reference-options categories (the same strings the API uses). */
export type FieldOptionCategory =
  | 'JobFamily' | 'CareerLevel' | 'ManagementLevel' | 'EducationLevel' | 'ExperienceRange' | 'WorkArrangement' | 'TravelRequirement' | 'Skill'
  | 'Certification' | 'License' | 'Language' | 'PhysicalRequirement' | 'WorkingCondition' | 'FlsaClassification' | 'BenefitEligibility'
  | 'RecommendedDepartment' | 'PerformanceIndicator';

export interface FieldValidation {
  maxLength?: number;
  /** A regular expression source (no flags): a declarative check, never executed from data. */
  pattern?: string;
  patternMessage?: string;
  /** For `list` / `multiselect`: the most items accepted. */
  maxItems?: number;
}

export interface RoleFieldDefinition {
  key: string;
  /** The wizard step: 1 Start, 2 Role details, 3 Responsibilities, 4 Qualifications, 5 Compensation, 6 Review. */
  step: 1 | 2 | 3 | 4 | 5 | 6;
  /** Position inside the step. */
  order: number;
  label: string;
  shortDefinition: string;
  whyWeAsk: string;
  example: string;
  requirement: FieldRequirement;
  inputType: FieldInputType;
  optionCategory?: FieldOptionCategory;
  customValuePolicy: CustomValuePolicy;
  /** Where the value or the options come from, in plain words. */
  dataSource: string;
  validation: FieldValidation;
  conditionalVisibility?: { dependsOn: string; equals: string };
  helpUrl?: { href: string; label: string };
  /** A length the screen suggests (not a limit). */
  recommendedLength?: { min?: number; max?: number; unit: 'characters' | 'items'; text: string };
}

export const STEP_NAMES: Record<number, string> = {
  1: 'Start', 2: 'Role details', 3: 'Responsibilities', 4: 'Qualifications', 5: 'Compensation (optional)', 6: 'Review',
};

const OWN = 'Typed by you. Saved with this role.';
const CUSTOM_OK = { customValuePolicy: 'allowedWithConfirm' as const };
const REFERENCE = (name: string) => `EPN standard options for ${name}, plus your company's own values. Loaded from the reference options list.`;
const NOT_LEGAL = 'This is your company’s own wording. EPN does not decide what the law or a regulator requires.';

/** The fields, in the order they appear. `fieldDef(key)` is the lookup the components use. */
export const ROLE_FIELDS: readonly RoleFieldDefinition[] = [
  // ---- Step 1: Start ----
  {
    key: 'startMode', step: 1, order: 1, label: 'How do you want to start?',
    shortDefinition: 'Start from an EPN template (starting text you then own) or from a blank role.',
    whyWeAsk: 'A template saves typing: it fills the description with sensible starting text. Nothing is copied until you choose Next, and you can change every word afterwards.',
    example: 'Choose a template when your role is common, such as an accountant or a customer support specialist. Choose blank when the role is unusual.',
    requirement: 'required', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice. Not saved with the role.', validation: {},
  },
  {
    key: 'template', step: 1, order: 2, label: 'EPN template',
    shortDefinition: 'An EPN-written starting description for a common role. When you use it, a copy becomes your own role; the template itself never changes.',
    whyWeAsk: 'So you do not have to write a job description from nothing. A template never sets anyone’s pay and never changes what anyone can do in EPN.',
    example: 'A payroll specialist template carries typical duties and qualifications for that kind of role.',
    requirement: 'optional', inputType: 'combobox', customValuePolicy: 'none', dataSource: 'The EPN template catalog (reviewed by EPN).',
    validation: {}, conditionalVisibility: { dependsOn: 'startMode', equals: 'template' },
  },

  // ---- Step 2: Role details ----
  {
    key: 'title', step: 2, order: 1, label: 'Role title',
    shortDefinition: 'The name of the job as your company uses it.',
    whyWeAsk: 'The title is how people find the role in lists, in Add Employee and on the employee profile. It is the only thing a draft needs.',
    example: 'Payroll Specialist',
    requirement: 'required', inputType: 'combobox', customValuePolicy: 'allowed', dataSource: 'Typed by you. Suggestions come from EPN templates and the official occupation list; picking one only fills the title.',
    validation: { maxLength: 150 },
  },
  {
    key: 'alternateTitles', step: 2, order: 2, label: 'Alternate titles',
    shortDefinition: 'Other names people use for the same job.',
    whyWeAsk: 'People search by the name they know. Alternate titles help them find the role, and help match it to official occupation names.',
    example: 'Payroll Clerk, Payroll Coordinator',
    requirement: 'optional', inputType: 'list', customValuePolicy: 'allowed', dataSource: OWN, validation: { maxLength: 150, maxItems: 20 },
    recommendedLength: { unit: 'items', text: 'One name per line.' },
  },
  {
    key: 'description', step: 2, order: 3, label: 'Description',
    shortDefinition: 'One or two sentences shown next to the role title in lists.',
    whyWeAsk: 'It is the short line people read first. The longer Summary below is the full overview used in the job description.',
    example: 'Prepares and checks payroll for hourly and salaried employees.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 500 },
    recommendedLength: { min: 40, max: 250, unit: 'characters', text: 'One or two sentences is plenty.' },
  },
  {
    key: 'roleCode', step: 2, order: 4, label: 'Internal code',
    shortDefinition: 'Your own short code for the role, unique in your company.',
    whyWeAsk: 'Many companies keep a code in their HR or finance records. It helps you match the role to those records. It must be different for every role.',
    example: 'FIN-PS2',
    requirement: 'optional', inputType: 'text', customValuePolicy: 'none',
    dataSource: 'Typed by you, or suggested from the department, title and level. It is never changed after you edit it.',
    validation: { maxLength: 40, pattern: '^[A-Za-z0-9][A-Za-z0-9._-]*$', patternMessage: 'Use letters, numbers, dots, dashes and underscores.' },
  },
  {
    key: 'departmentId', step: 2, order: 5, label: 'Department',
    shortDefinition: 'The part of your company the role belongs to.',
    whyWeAsk: 'It groups roles in lists and suggests a department when you add an employee. It is never assigned for you, and it does not change anyone’s access.',
    example: 'Finance',
    requirement: 'recommended', inputType: 'department', customValuePolicy: 'none',
    dataSource: 'Your company’s departments. A department you add here is saved for the whole company when you save it.', validation: {},
  },
  {
    key: 'departmentName', step: 2, order: 5.1, label: 'Department name',
    shortDefinition: 'The name of the new department, as people in your company say it.',
    whyWeAsk: 'A department needs a name to be chosen from lists. A name that already exists (in any capitalization) cannot be added twice.',
    example: 'Customer Success',
    requirement: 'required', inputType: 'text', customValuePolicy: 'none', dataSource: 'Typed by you. Saved for your whole company when you save the department.', validation: { maxLength: 100 },
  },
  {
    key: 'departmentCode', step: 2, order: 5.2, label: 'Department code',
    shortDefinition: 'A short code for the department, letters and numbers, unique in your company.',
    whyWeAsk: 'The code is used to suggest role codes and to match your own records. EPN suggests one from the name; you can change it, and it is never changed after you edit it.',
    example: 'CS',
    requirement: 'required', inputType: 'text', customValuePolicy: 'none', dataSource: 'Suggested from the name, and editable. It is saved in upper case.',
    validation: { maxLength: 20, pattern: '^[A-Z0-9][A-Z0-9_-]{1,19}$', patternMessage: 'Use 2 to 20 letters, numbers, dashes or underscores, starting with a letter or number.' },
  },
  {
    key: 'departmentDescription', step: 2, order: 5.3, label: 'Department description',
    shortDefinition: 'A sentence on what the department does.',
    whyWeAsk: 'It helps the people who choose a department later to pick the right one.',
    example: 'Helps customers get lasting value after they sign up.',
    requirement: 'required', inputType: 'textarea', customValuePolicy: 'none', dataSource: 'Typed by you. Saved for your whole company when you save the department.', validation: { maxLength: 500 },
  },
  {
    key: 'jobFamily', step: 2, order: 6, label: 'Job family',
    shortDefinition: 'The broad group of related jobs the role belongs to.',
    whyWeAsk: 'Families let you compare and organize similar roles across departments.',
    example: 'Finance and Accounting',
    requirement: 'recommended', inputType: 'combobox', optionCategory: 'JobFamily', ...CUSTOM_OK, dataSource: REFERENCE('job families'), validation: { maxLength: 100 },
  },
  {
    key: 'careerLevel', step: 2, order: 7, label: 'Level',
    shortDefinition: 'How experienced or senior the role is on your career ladder.',
    whyWeAsk: 'It shows how roles relate to each other. Level describes the job, not the person, and it is not management authority: that is the next field.',
    example: 'Intermediate',
    requirement: 'recommended', inputType: 'select', optionCategory: 'CareerLevel', customValuePolicy: 'none',
    dataSource: 'EPN standard career levels, in order. This list is controlled by EPN, so your own values cannot be added.', validation: { maxLength: 100 },
  },
  {
    key: 'managementLevel', step: 2, order: 8, label: 'Management level',
    shortDefinition: 'How much of other people’s work the role is accountable for.',
    whyWeAsk: 'It is separate from level: a senior specialist can have a high level and still manage nobody.',
    example: 'Individual Contributor',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'ManagementLevel', ...CUSTOM_OK, dataSource: REFERENCE('management levels'), validation: { maxLength: 100 },
  },
  {
    key: 'socCode', step: 2, order: 9, label: 'SOC code',
    shortDefinition: 'The Standard Occupational Classification code of the closest official occupation.',
    whyWeAsk: 'Market benchmarks are looked up by SOC code, and it keeps your roles comparable. You choose it from the official list; it is never filled in for you.',
    example: '43-3051 Payroll and Timekeeping Clerks',
    requirement: 'recommended', inputType: 'occupation', customValuePolicy: 'none',
    dataSource: 'The official occupation list loaded into EPN (name and version shown beside the lookup). Typed codes are not accepted: you pick from the list.',
    validation: { pattern: '^\\d{2}-\\d{4}$', patternMessage: 'Pick a code from the official list.' },
    helpUrl: { href: 'https://www.bls.gov/soc/', label: 'About the Standard Occupational Classification' },
  },
  {
    key: 'socStatus', step: 2, order: 10, label: 'SOC classification status',
    shortDefinition: 'Whether you have confirmed a SOC code for the role, or are leaving it for now.',
    whyWeAsk: 'Choosing “Not yet classified” is allowed. It records that the gap is deliberate, with your reason.',
    example: 'Not yet classified, because HR is still reviewing the role.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none',
    dataSource: 'Set when you choose a code or mark it not yet classified. The dataset version is recorded by the server when you save.', validation: {},
  },
  {
    key: 'socNotYetClassifiedReason', step: 2, order: 11, label: 'Why is the SOC code not yet classified?',
    shortDefinition: 'A short reason you are not choosing a SOC code now.',
    whyWeAsk: 'So anyone reading the role later knows the gap was a choice and not an oversight.',
    example: 'Waiting for HR to confirm the closest occupation.',
    requirement: 'required', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 300 },
    conditionalVisibility: { dependsOn: 'socStatus', equals: 'NotYetClassified' },
  },
  {
    key: 'onetCode', step: 2, order: 12, label: 'O*NET code',
    shortDefinition: 'The O*NET-SOC code of the closest detailed occupation (the SOC code plus two digits).',
    whyWeAsk: 'O*NET is a more detailed list than SOC. Recording it makes the role easier to compare with public occupation information.',
    example: '43-3051.00 Payroll and Timekeeping Clerks',
    requirement: 'optional', inputType: 'occupation', customValuePolicy: 'none',
    dataSource: 'The official O*NET occupation list loaded into EPN. After you choose a SOC code, suggestions are limited to it.',
    validation: { pattern: '^\\d{2}-\\d{4}\\.\\d{2}$', patternMessage: 'Pick a code from the official list.' },
    helpUrl: { href: 'https://www.onetonline.org/', label: 'About O*NET OnLine' },
  },
  {
    key: 'onetStatus', step: 2, order: 13, label: 'O*NET classification status',
    shortDefinition: 'Whether you have confirmed an O*NET code for the role, or are leaving it for now.',
    whyWeAsk: 'Choosing “Not yet classified” is allowed. It records that the gap is deliberate, with your reason.',
    example: 'Not yet classified, because the SOC code is enough for now.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none',
    dataSource: 'Set when you choose a code or mark it not yet classified. The dataset version is recorded by the server when you save.', validation: {},
  },
  {
    key: 'onetNotYetClassifiedReason', step: 2, order: 14, label: 'Why is the O*NET code not yet classified?',
    shortDefinition: 'A short reason you are not choosing an O*NET code now.',
    whyWeAsk: 'So anyone reading the role later knows the gap was a choice and not an oversight.',
    example: 'The SOC code is enough for now.',
    requirement: 'required', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 300 },
    conditionalVisibility: { dependsOn: 'onetStatus', equals: 'NotYetClassified' },
  },
  {
    key: 'summary', step: 2, order: 15, label: 'Summary',
    shortDefinition: 'A short overview of the role: why it exists, what it is accountable for, who it supports and what good looks like.',
    whyWeAsk: 'The summary opens the job description. A role needs one before it can be activated and offered when you add an employee.',
    example: 'Prepares and reviews payroll inputs, answers employee pay questions and supports payroll close.',
    requirement: 'requiredToActivate', inputType: 'textarea', customValuePolicy: 'none', dataSource: 'Typed by you, or inserted from the template when you choose “Use suggested summary”.',
    validation: { maxLength: 4000 },
    recommendedLength: { min: 150, max: 600, unit: 'characters', text: 'A short paragraph (about 2 to 4 sentences) is usually enough.' },
  },
  {
    key: 'purpose', step: 2, order: 16, label: 'Purpose',
    shortDefinition: 'One sentence on why the role exists.',
    whyWeAsk: 'The purpose is the reason the role is on your organization chart. It keeps the long lists of duties pointing at one goal.',
    example: 'Keeps each payroll cycle accurate, on time and well documented.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 1000 },
    recommendedLength: { min: 40, max: 250, unit: 'characters', text: 'One sentence is enough.' },
  },

  // ---- Step 3: Responsibilities ----
  {
    key: 'essentialDuties', step: 3, order: 1, label: 'Essential duties',
    shortDefinition: 'The main things the role must be able to do. Without them the job cannot be done.',
    whyWeAsk: 'Essential duties are the heart of a job description. A role needs at least one before it can be activated.',
    example: 'Review payroll registers and investigate differences.',
    requirement: 'requiredToActivate', inputType: 'list', customValuePolicy: 'allowed',
    dataSource: 'Typed by you, or added from the template’s own list. Nothing is added for you.', validation: { maxLength: 500, maxItems: 50 },
    recommendedLength: { min: 3, max: 10, unit: 'items', text: 'Roughly 3 to 10 clear duties.' },
  },
  {
    key: 'responsibilities', step: 3, order: 2, label: 'Additional responsibilities',
    shortDefinition: 'Regular tasks that are part of the role but not essential to doing the job.',
    whyWeAsk: 'Separating them from essential duties makes the role easier to read and keeps the essential list short.',
    example: 'Support audits and period-end reconciliations.',
    requirement: 'optional', inputType: 'list', customValuePolicy: 'allowed', dataSource: OWN, validation: { maxLength: 500, maxItems: 50 },
  },
  {
    key: 'expectedOutcomes', step: 3, order: 3, label: 'Expected outcomes',
    shortDefinition: 'What good work looks like: the results the role is expected to achieve.',
    whyWeAsk: 'Outcomes tell the person and their manager how success is seen, in plain terms and without a rating system.',
    example: 'Payroll is submitted for approval on schedule.',
    requirement: 'optional', inputType: 'list', customValuePolicy: 'allowed', dataSource: OWN, validation: { maxLength: 500, maxItems: 50 },
  },
  {
    key: 'decisionAuthority', step: 3, order: 4, label: 'Decision-making authority',
    shortDefinition: 'The decisions the role can make on its own, and the ones it must escalate.',
    whyWeAsk: 'Clear authority avoids delays and mistakes. It describes the work; it does not grant any permission in EPN.',
    example: 'Approves routine pay corrections up to your own policy limit and escalates exceptions to the manager.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 2000 },
  },
  {
    key: 'budgetAuthority', step: 3, order: 5, label: 'Budget authority',
    shortDefinition: 'Whether the role controls or approves spending, and in what way.',
    whyWeAsk: 'Roles that approve spending carry more responsibility, and people considering the job want to know. Write it in words; no amounts are needed.',
    example: 'Approves team purchases within the department budget.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 500 },
  },
  {
    key: 'supervisoryResponsibilities', step: 3, order: 6, label: 'Supervisory responsibilities',
    shortDefinition: 'Which people, if any, the role manages, and how.',
    whyWeAsk: 'It shows the size and kind of leadership in the role. Leave it empty when the role manages nobody.',
    example: 'Manages three payroll specialists: assigns work and reviews their output.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 2000 },
  },
  {
    key: 'internalRelationships', step: 3, order: 7, label: 'Internal relationships',
    shortDefinition: 'The teams inside your company the role works with regularly.',
    whyWeAsk: 'It shows who the role depends on and who depends on it.',
    example: 'Works daily with HR and accounting.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 2000 },
  },
  {
    key: 'externalRelationships', step: 3, order: 8, label: 'External relationships',
    shortDefinition: 'The people outside your company the role deals with.',
    whyWeAsk: 'It tells a reader whether the role is customer-facing or works with vendors, regulators or advisors.',
    example: 'Works with the payroll tax agency and the company’s accountant.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 2000 },
  },
  {
    key: 'complianceResponsibilities', step: 3, order: 9, label: 'Compliance responsibilities',
    shortDefinition: 'Rules, policies or reporting duties the role is responsible for following or enforcing.',
    whyWeAsk: `It records the compliance part of the job in your own words. ${NOT_LEGAL}`,
    example: 'Keeps payroll records for the period your company policy requires.',
    requirement: 'optional', inputType: 'list', customValuePolicy: 'allowed', dataSource: OWN, validation: { maxLength: 500, maxItems: 50 },
    recommendedLength: { unit: 'items', text: 'One item per line.' },
  },
  {
    key: 'performanceIndicators', step: 3, order: 10, label: 'Performance indicators',
    shortDefinition: 'Generic measures of how the role is doing, such as accuracy or response time.',
    whyWeAsk: 'Indicators make outcomes concrete. They are examples to pick from and adapt; they are not targets and not an employee rating.',
    example: 'Payroll accuracy rate',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'PerformanceIndicator', ...CUSTOM_OK, dataSource: REFERENCE('performance indicators'),
    validation: { maxLength: 200, maxItems: 30 },
  },
  {
    key: 'reportsTo', step: 3, order: 11, label: 'Reports to',
    shortDefinition: 'The role (not a person) this role reports to.',
    whyWeAsk: 'It shows where the role sits. Name a job, not a person, so the description stays true when people change.',
    example: 'Payroll Manager',
    requirement: 'optional', inputType: 'text', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 150 },
  },

  // ---- Step 4: Qualifications ----
  {
    key: 'minimumEducation', step: 4, order: 1, label: 'Minimum education',
    shortDefinition: 'The least formal education the role asks for.',
    whyWeAsk: `It tells candidates whether they qualify. Choose “Equivalent experience accepted” if experience can stand in. ${NOT_LEGAL}`,
    example: 'High school diploma or equivalent',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'EducationLevel', ...CUSTOM_OK, dataSource: REFERENCE('education levels'), validation: { maxLength: 200 },
  },
  {
    key: 'preferredEducation', step: 4, order: 2, label: 'Preferred education',
    shortDefinition: 'Education that is welcome but not required.',
    whyWeAsk: 'It separates what the job needs from what would be a plus.',
    example: 'Bachelor’s degree',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'EducationLevel', ...CUSTOM_OK, dataSource: REFERENCE('education levels'), validation: { maxLength: 200 },
  },
  {
    key: 'minimumExperience', step: 4, order: 3, label: 'Minimum experience',
    shortDefinition: 'The least relevant work experience the role asks for.',
    whyWeAsk: 'It sets a fair starting point for candidates and managers.',
    example: '2-4 years',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'ExperienceRange', ...CUSTOM_OK, dataSource: REFERENCE('experience ranges'), validation: { maxLength: 200 },
  },
  {
    key: 'preferredExperience', step: 4, order: 4, label: 'Preferred experience',
    shortDefinition: 'Experience that is welcome but not required.',
    whyWeAsk: 'It separates what the job needs from what would be a plus.',
    example: '5-7 years',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'ExperienceRange', ...CUSTOM_OK, dataSource: REFERENCE('experience ranges'), validation: { maxLength: 200 },
  },
  {
    key: 'technicalSkills', step: 4, order: 5, label: 'Technical skills',
    shortDefinition: 'Specific tools, systems or methods the person needs to use.',
    whyWeAsk: 'Listing them helps with hiring and with training plans.',
    example: 'Payroll software',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'Skill', ...CUSTOM_OK, dataSource: REFERENCE('skills'), validation: { maxLength: 100, maxItems: 50 },
  },
  {
    key: 'professionalSkills', step: 4, order: 6, label: 'Professional skills',
    shortDefinition: 'Everyday working skills such as communication or organization.',
    whyWeAsk: 'These matter as much as technical skills in most jobs.',
    example: 'Attention to detail',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'Skill', ...CUSTOM_OK, dataSource: REFERENCE('skills'), validation: { maxLength: 100, maxItems: 50 },
  },
  {
    key: 'preferredSkills', step: 4, order: 7, label: 'Preferred skills',
    shortDefinition: 'Skills that are welcome but not required.',
    whyWeAsk: 'They separate what the job needs from what would be a plus.',
    example: 'Spreadsheets',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'Skill', ...CUSTOM_OK, dataSource: REFERENCE('skills'), validation: { maxLength: 100, maxItems: 50 },
  },
  {
    key: 'certifications', step: 4, order: 8, label: 'Certifications',
    shortDefinition: 'Professional credentials the role asks for or welcomes.',
    whyWeAsk: 'It tells candidates what credentials count. A credential you pick here is what your company chooses; EPN does not say the law requires it.',
    example: 'Certified Payroll Professional (CPP)',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'Certification', ...CUSTOM_OK,
    dataSource: `${REFERENCE('certifications')} They are examples only.`, validation: { maxLength: 100, maxItems: 50 },
  },
  {
    key: 'licenses', step: 4, order: 9, label: 'Licenses',
    shortDefinition: 'Licenses the role asks for or welcomes.',
    whyWeAsk: 'It tells candidates what licenses count. A license you pick here is what your company chooses; EPN does not say the law requires it.',
    example: 'Driver’s license',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'License', ...CUSTOM_OK,
    dataSource: `${REFERENCE('licenses')} They are examples only.`, validation: { maxLength: 100, maxItems: 50 },
  },
  {
    key: 'languages', step: 4, order: 10, label: 'Languages',
    shortDefinition: 'Languages the role uses at work.',
    whyWeAsk: 'It matters for customer-facing roles and for teams that work in more than one language.',
    example: 'Spanish',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'Language', ...CUSTOM_OK, dataSource: REFERENCE('languages'), validation: { maxLength: 100, maxItems: 30 },
  },
  {
    key: 'travel', step: 4, order: 11, label: 'Travel',
    shortDefinition: 'How much of the working time involves travel.',
    whyWeAsk: 'Candidates need to know before they apply.',
    example: 'Occasional, up to 10%',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'TravelRequirement', ...CUSTOM_OK, dataSource: REFERENCE('travel requirements'), validation: { maxLength: 200 },
  },
  {
    key: 'workArrangement', step: 4, order: 12, label: 'Work arrangement',
    shortDefinition: 'Where the work is usually done.',
    whyWeAsk: 'On-site, hybrid and remote roles suit different people.',
    example: 'Hybrid',
    requirement: 'optional', inputType: 'combobox', optionCategory: 'WorkArrangement', ...CUSTOM_OK, dataSource: REFERENCE('work arrangements'), validation: { maxLength: 200 },
  },
  {
    key: 'physicalRequirements', step: 4, order: 13, label: 'Physical requirements',
    shortDefinition: 'The physical demands of the job.',
    whyWeAsk: `It helps people judge whether they can do the work, with or without adjustments. ${NOT_LEGAL}`,
    example: 'Lifting up to 25 pounds',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'PhysicalRequirement', ...CUSTOM_OK, dataSource: REFERENCE('physical requirements'),
    validation: { maxLength: 200, maxItems: 30 },
  },
  {
    key: 'workingConditions', step: 4, order: 14, label: 'Working conditions',
    shortDefinition: 'The environment and conditions the work is done in.',
    whyWeAsk: 'Noise, hours and place of work shape the job as much as the tasks do.',
    example: 'Office environment',
    requirement: 'optional', inputType: 'multiselect', optionCategory: 'WorkingCondition', ...CUSTOM_OK, dataSource: REFERENCE('working conditions'),
    validation: { maxLength: 200, maxItems: 30 },
  },

  // ---- Step 5: Compensation (optional) ----
  {
    key: 'typicalEmploymentType', step: 5, order: 1, label: 'Typical employment type',
    shortDefinition: 'How people in this role are usually employed.',
    whyWeAsk: 'It is a hint shown when you add an employee. It never sets anyone’s employment type.',
    example: 'Full-time',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'A short list of common types. A saved value outside the list is kept.', validation: { maxLength: 100 },
  },
  {
    key: 'typicalPayType', step: 5, order: 2, label: 'Typical pay type',
    shortDefinition: 'How people in this role are usually paid.',
    whyWeAsk: 'It is a hint shown when you add an employee. It is never a rate and never sets anyone’s pay.',
    example: 'Salary',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'A short list of common pay types. A saved value outside the list is kept.', validation: { maxLength: 100 },
  },
  {
    key: 'hourlyOrSalaried', step: 5, order: 3, label: 'Hourly or salaried',
    shortDefinition: 'Whether people in this role are usually paid by the hour or on a salary.',
    whyWeAsk: 'It is a hint for planning. It is not a legal classification, and it never sets anyone’s pay.',
    example: 'Hourly',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'A short list. A saved value outside the list is kept.', validation: { maxLength: 100 },
  },
  {
    key: 'currency', step: 5, order: 4, label: 'Currency',
    shortDefinition: 'The currency of the pay band and the benchmarks. EPN supports US dollars only.',
    whyWeAsk: 'It is shown so there is no doubt about the unit. It cannot be changed.',
    example: 'USD (US dollars)',
    requirement: 'optional', inputType: 'readonly', customValuePolicy: 'none', dataSource: 'Fixed by EPN.', validation: {},
  },
  {
    key: 'payBandBasis', step: 5, order: 5, label: 'Pay basis',
    shortDefinition: 'Whether your pay band is written as an hourly rate or an annual salary.',
    whyWeAsk: 'The numbers below only make sense with a unit. EPN can show the other unit using the annual hours you set.',
    example: 'Annual (a salary per year)',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice.', validation: {},
  },
  {
    key: 'payBandMinimum', step: 5, order: 6, label: 'Pay band minimum',
    shortDefinition: 'The lowest amount your company plans to pay for this role.',
    whyWeAsk: 'It is your own range: a guide for your own planning, not an employee’s pay. Adding it never changes anyone’s rate.',
    example: 'An amount in the unit you chose above.',
    requirement: 'optional', inputType: 'money', customValuePolicy: 'none', dataSource: 'Typed by you. Saved as your company’s pay band, which is kept in a history.', validation: {},
  },
  {
    key: 'payBandMidpoint', step: 5, order: 7, label: 'Pay band midpoint',
    shortDefinition: 'The middle of your pay range for this role.',
    whyWeAsk: 'Many companies plan around the midpoint. It must be between the minimum and the maximum.',
    example: 'An amount between your minimum and maximum.',
    requirement: 'optional', inputType: 'money', customValuePolicy: 'none', dataSource: 'Typed by you.', validation: {},
  },
  {
    key: 'payBandMaximum', step: 5, order: 8, label: 'Pay band maximum',
    shortDefinition: 'The highest amount your company plans to pay for this role.',
    whyWeAsk: 'It completes your own range. It is a guide, not an employee’s pay, and it does not override any law about pay.',
    example: 'An amount at or above your midpoint.',
    requirement: 'optional', inputType: 'money', customValuePolicy: 'none', dataSource: 'Typed by you.', validation: {},
  },
  {
    key: 'payBandAnnualHours', step: 5, order: 9, label: 'Annual hours assumption',
    shortDefinition: 'The number of working hours in a year used to convert between hourly and annual amounts.',
    whyWeAsk: 'Converting a rate to a salary needs a number of hours. 2,080 is a 40-hour week for 52 weeks. It is only used for converting.',
    example: '2,080',
    requirement: 'optional', inputType: 'number', customValuePolicy: 'none', dataSource: 'Typed by you. The default is 2,080.', validation: {},
  },
  {
    key: 'payBandNote', step: 5, order: 10, label: 'Pay band note',
    shortDefinition: 'A short note about the band, for your own records.',
    whyWeAsk: 'It records context such as when the band was reviewed. Do not include personal information about any person.',
    example: 'Reviewed with the annual pay review.',
    requirement: 'optional', inputType: 'textarea', customValuePolicy: 'none', dataSource: OWN, validation: { maxLength: 500 },
  },
  {
    key: 'payBandReviewed', step: 5, order: 11, label: 'Pay band review confirmation',
    shortDefinition: 'Your confirmation that you reviewed the band before saving it.',
    whyWeAsk: 'A pay band is company policy. EPN asks you to confirm you reviewed it, every time it is set or changed.',
    example: 'Tick the box once you are happy with the numbers.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your confirmation. Required whenever a band is saved.', validation: {},
  },
  {
    key: 'marketLow', step: 5, order: 12, label: 'Market low',
    shortDefinition: 'A low point reported by a named survey source for this occupation, read-only.',
    whyWeAsk: 'It is reference data. It is a survey percentile, not the lowest salary anyone is paid, and it does not set or limit your pay band.',
    example: 'A source might report its 10th percentile here.',
    requirement: 'optional', inputType: 'readonly', customValuePolicy: 'none',
    dataSource: 'A named benchmark source, with its geography, year and percentile shown beside the number. Nothing is estimated.', validation: {},
  },
  {
    key: 'marketMedian', step: 5, order: 13, label: 'Market median',
    shortDefinition: 'The middle point reported by a named survey source for this occupation, read-only.',
    whyWeAsk: 'It shows the middle of the source’s survey. It is a percentile, not a recommended wage, and it does not replace minimum wage, overtime, equal pay or pay transparency rules.',
    example: 'A source might report its 50th percentile here.',
    requirement: 'optional', inputType: 'readonly', customValuePolicy: 'none',
    dataSource: 'A named benchmark source, with its geography, year and percentile shown beside the number.', validation: {},
  },
  {
    key: 'marketHigh', step: 5, order: 14, label: 'Market high',
    shortDefinition: 'A high point reported by a named survey source for this occupation, read-only.',
    whyWeAsk: 'It is reference data. It is a survey percentile, not the highest salary anyone is paid, and it does not cap your pay band.',
    example: 'A source might report its 90th percentile here.',
    requirement: 'optional', inputType: 'readonly', customValuePolicy: 'none',
    dataSource: 'A named benchmark source, with its geography, year and percentile shown beside the number.', validation: {},
  },
  {
    key: 'benchmarkGeography', step: 5, order: 15, label: 'Benchmark geography',
    shortDefinition: 'The area the market benchmark covers: national, a state or a metro area.',
    whyWeAsk: 'Pay differs by place. If the source has no data for the area you ask for, EPN shows the nearest wider area and says so.',
    example: 'State: California',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice. Not saved with the role.', validation: {},
  },
  {
    key: 'benchmarkReferenceYear', step: 5, order: 16, label: 'Benchmark reference year',
    shortDefinition: 'The year of the survey the benchmark comes from.',
    whyWeAsk: 'Older surveys are less current. Leave it empty to see the latest year available.',
    example: '2025',
    requirement: 'optional', inputType: 'text', customValuePolicy: 'none', dataSource: 'Your choice. Not saved with the role.', validation: { pattern: '^\\d{4}$', patternMessage: 'Enter four digits.' },
  },
  {
    key: 'benchmarkBasis', step: 5, order: 17, label: 'Show benchmark values as',
    shortDefinition: 'Whether the benchmark is shown as hourly or annual amounts.',
    whyWeAsk: 'So you can compare it with your own band in the same unit.',
    example: 'Annual',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice. Not saved with the role.', validation: {},
  },
  {
    key: 'benchmarkAnnualHours', step: 5, order: 18, label: 'Benchmark annual hours',
    shortDefinition: 'The hours per year used only to convert benchmark values between hourly and annual.',
    whyWeAsk: 'The conversion is a plain multiplication or division by this number. Nothing else is adjusted.',
    example: '2,080',
    requirement: 'optional', inputType: 'number', customValuePolicy: 'none', dataSource: 'Your choice. The default is shown. Not saved with the role.', validation: {},
  },
  {
    key: 'bonusEligible', step: 5, order: 19, label: 'Bonus eligible',
    shortDefinition: 'Whether people in this role are usually eligible for a bonus under your company’s plan.',
    whyWeAsk: 'It is a hint for planning. It never promises a bonus and never sets anyone’s pay.',
    example: 'Yes, under the annual bonus plan.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice: Yes, No or Not stated.', validation: {},
  },
  {
    key: 'commissionEligible', step: 5, order: 20, label: 'Commission eligible',
    shortDefinition: 'Whether people in this role are usually paid commission under your company’s plan.',
    whyWeAsk: 'It is a hint for planning. It never promises commission and never sets anyone’s pay.',
    example: 'Yes for sales roles.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice: Yes, No or Not stated.', validation: {},
  },
  {
    key: 'overtimeEligible', step: 5, order: 21, label: 'Overtime eligible (employer classification)',
    shortDefinition: 'Your company’s own classification of whether the role is eligible for overtime.',
    whyWeAsk: 'It records your decision. EPN does not decide overtime eligibility: review it with your payroll or legal advisor.',
    example: 'No, once your advisor confirms the role is exempt.',
    requirement: 'optional', inputType: 'select', customValuePolicy: 'none', dataSource: 'Your choice: Yes, No or Not stated. It is your own classification, and EPN does not make the legal decision.', validation: {},
    helpUrl: { href: 'https://www.dol.gov/agencies/whd/flsa', label: 'About the Fair Labor Standards Act' },
  },
  {
    key: 'flsaClassification', step: 5, order: 22, label: 'FLSA classification (employer-selected)',
    shortDefinition: 'Your company’s own choice of exempt or non-exempt for the role under the Fair Labor Standards Act.',
    whyWeAsk: 'It records your decision for reference. EPN never determines it for you and does not give legal advice: review it with your payroll or legal advisor.',
    example: 'Not yet determined (review with a payroll or legal advisor)',
    requirement: 'optional', inputType: 'select', optionCategory: 'FlsaClassification', customValuePolicy: 'none',
    dataSource: 'EPN employer-classification labels. This list is controlled by EPN, so your own values cannot be added.', validation: { maxLength: 200 },
    helpUrl: { href: 'https://www.dol.gov/agencies/whd/flsa', label: 'About the Fair Labor Standards Act' },
  },
  {
    key: 'benefitEligibilityGuidance', step: 5, order: 23, label: 'Benefit eligibility guidance',
    shortDefinition: 'A short note on how your benefit plans usually apply to the role.',
    whyWeAsk: 'It helps people understand the role. It does not enroll anyone and it does not decide who is eligible: your plan documents do.',
    example: 'Follows the company benefits policy for the employee’s employment type.',
    requirement: 'optional', inputType: 'textarea', optionCategory: 'BenefitEligibility', customValuePolicy: 'allowed',
    dataSource: 'Typed by you; you can add a standard statement from the list, which is added as a new line and never replaces your text.',
    validation: { maxLength: 2000 },
  },

  // ---- Step 6: Review ----
  {
    key: 'activationReadiness', step: 6, order: 1, label: 'Activation',
    shortDefinition: 'Saving a draft keeps the role private to you; activating makes it available to give to employees.',
    whyWeAsk: 'A draft needs only a title. To activate, a role also needs a summary and at least one essential duty. Review lists anything missing.',
    example: 'Save as draft now, finish later, then activate.',
    requirement: 'optional', inputType: 'readonly', customValuePolicy: 'none', dataSource: 'The rules of the Job Role Library. They are checked again on the server.', validation: {},
  },
];

const BY_KEY: ReadonlyMap<string, RoleFieldDefinition> = new Map(ROLE_FIELDS.map((f) => [f.key, f]));

export function fieldDef(key: string): RoleFieldDefinition {
  const found = BY_KEY.get(key);
  if (!found) throw new Error(`No registry entry for the role field "${key}". Add it to roleFieldRegistry.ts.`);
  return found;
}

export const hasFieldDef = (key: string): boolean => BY_KEY.has(key);

export const fieldsOfStep = (step: number): RoleFieldDefinition[] => ROLE_FIELDS.filter((f) => f.step === step).sort((a, b) => a.order - b.order);

/** The wording of a requirement as the screen shows it (text, never colour alone). */
export const REQUIREMENT_LABEL: Record<FieldRequirement, string> = {
  required: 'Required', requiredToActivate: 'Required to activate', recommended: 'Recommended', optional: 'Optional',
};
